/**
 * Shared presentational primitives for the Organisation console.
 *
 * Small, single-purpose pieces composed by every view so the four mandatory
 * async states (error / loading / empty / data — see frontend/CLAUDE.md) and the
 * visual language are expressed once, not re-implemented per section.
 *
 * Aesthetic: relief from SAP. Hairline surfaces, one Action-Blue accent,
 * generous whitespace, friendly empty states. Density only where it earns its
 * keep (the tree).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { sanitizeErrorMessage } from '@/utils/errorMessages';
import { ApiError } from '@/api/client';
import type { OrgRole } from '@/api/endpoints/org';

/** Human-readable, sanitised message from any thrown value. */
export function errMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : 'Unknown error';
  return sanitizeErrorMessage(msg);
}

/** A 4xx is a caller error — retrying reproduces it. Network (status 0) / 5xx retry. */
export function isRetriable(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === 0 || err.status >= 500;
  return true;
}

/**
 * True for a 403 — the caller lacks access to the unit (panel read or unit
 * action). The deny is the trigger for the "Request access" affordance: the
 * server's message even points the user at it.
 */
export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

const roleVariant: Record<OrgRole, 'default' | 'secondary' | 'outline'> = {
  owner: 'default',
  editor: 'secondary',
  viewer: 'outline',
};

export function RoleBadge({ role }: { role: OrgRole }) {
  const { t } = useTranslation();
  return <Badge variant={roleVariant[role]} className="capitalize">{t(`org.accessLevel.${role}`, role)}</Badge>;
}

/**
 * A small rolled-up patient-count badge for a unit (tree row / breakdown). The
 * census is a count, not a roster — a quiet neutral pill, distinct from the
 * Action-Blue KindChip so it never competes for the eye. Render only when the
 * count is known and non-zero (the caller decides); a unit with no patients
 * stays badge-free.
 */
export function CountBadge({ count, context }: { count: number; context?: string }) {
  const { t } = useTranslation();
  // The plural rule lives here only — callers pass an optional context suffix
  // (e.g. "in Klinikum 2 and its sub-units") rather than re-spelling the count.
  const base = t('org.ui.patientCount', '{{count}} patients', { count });
  const text = context ? `${base} ${context}` : base;
  return (
    <span
      title={text}
      aria-label={text}
      className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-neutral-500"
    >
      {count}
    </span>
  );
}

/** Subtle, kind-agnostic chip — the vocabulary is config-driven, so no per-kind colour map. */
export function KindChip({ kind }: { kind: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary">
      {kind}
    </span>
  );
}

/** Section heading with an optional right-aligned action (e.g. "+ Add"). */
export function SectionHeader({
  title,
  count,
  action,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {title}
        {count !== undefined && <span className="ml-2 font-normal text-neutral-400">{count}</span>}
      </h3>
      {action}
    </div>
  );
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-lg" />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
      {errMessage(error)}
      {onRetry && isRetriable(error) && (
        <button className="ml-2 underline underline-offset-2" onClick={onRetry}>
          {t('actions.retry', 'Retry')}
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-neutral-200 px-6 py-10 text-center">
      <p className="text-sm font-medium text-neutral-700">{title}</p>
      {hint && <p className="max-w-sm text-xs text-neutral-400">{hint}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** A flat, hairline-bordered card — the console's one surface treatment. */
export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-xl border border-neutral-200 bg-white', className)}>{children}</div>
  );
}

/** A removable list row: label on the left, a quiet × on the right. */
export function RemovableRow({
  children,
  onRemove,
  removing,
  removeLabel,
}: {
  children: ReactNode;
  onRemove: () => void;
  removing?: boolean;
  removeLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-neutral-50">
      <div className="min-w-0 flex-1">{children}</div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-neutral-400 hover:text-red-600"
        onClick={onRemove}
        disabled={removing}
        aria-label={removeLabel ?? t('org.ui.remove', 'Remove')}
      >
        ✕
      </Button>
    </div>
  );
}
